import { lazy } from 'react';
import type { ComponentType } from 'react';

/**
 * `React.lazy`, for a page whose chunk can stop existing.
 *
 * Since routes were split, each one is a hashed file fetched on navigation. A deploy replaces those
 * hashes, so anyone holding the previous `index.html` asks for a name that is no longer on the
 * server: `Failed to fetch dynamically imported module`. Nothing is broken — the tab is simply out
 * of date, and the answer is a reload, not an error screen.
 *
 * So: retry once, which covers a dropped connection, then reload, which covers the deploy.
 */

const RELOAD_KEY = 'dx-chunk-reloaded-at';
const RELOAD_COOLDOWN_MS = 30_000;

/** sessionStorage throws in private mode and with storage blocked, and this runs inside a failure
 *  path already — it must not add a second one. Refusing the reload is the safe answer. */
const reloadedRecently = (): boolean => {
  try {
    const at = Number(window.sessionStorage.getItem(RELOAD_KEY));
    return at > 0 && Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    return true;
  }
};

const markReloaded = (): boolean => {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

type Loader<T> = () => Promise<{ default: T }>;

export function lazyWithRetry<T extends ComponentType<any>>(loader: Loader<T>) {
  return lazy(async () => {
    try {
      return await loader();
    } catch {
      try {
        return await loader();
      } catch (secondError) {
        if (reloadedRecently() || !markReloaded()) throw secondError;
        window.location.reload();
        /* The page is going away, so this promise is never settled. Resolving would render a
           component that is about to be torn down; rejecting would flash the error boundary the
           reload exists to avoid. */
        return new Promise<{ default: T }>(() => {});
      }
    }
  });
}
